import { useMemo, useState } from 'react';
import { useCurrentUser } from '@/features/auth/api';
import { can } from '@/lib/permissions';

import { ClipboardList } from 'lucide-react';

import type { Meeting, MeetingStatus, MeetingType } from '@/api/schema';
import { ApiError } from '@/api/client';
import { Alert } from '@/components/feedback/Alert';
import { useToast } from '@/components/feedback/Toast';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { EmptyState, ErrorState } from '@/components/ui/EmptyState';
import { Select, Textarea } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useDateFormat } from '@/features/company/api';
import { formatDateTime } from '@/lib/dates';

import { useLogMeeting, useMeetings, useMeetingsNeedingALog } from '../api';
import {
  MEETING_STATUS_LABEL,
  MEETING_STATUS_OPTIONS,
  MEETING_STATUS_TONE,
  MEETING_TYPE_LABEL,
  MEETING_TYPE_OPTIONS,
  MEETING_TYPE_TONE,
} from '../labels';
import styles from './MeetingLogPage.module.css';

/** Only real outcomes: "scheduled" is where a meeting starts, not what came of it. */
const OUTCOME_OPTIONS = MEETING_STATUS_OPTIONS.filter((option) => option.value !== 'scheduled');

/**
 * Every meeting, newest first, and the ones still waiting to be written up.
 *
 * The unlogged list is deliberately at the top and not merely a filter. A meeting
 * nobody recorded is a question outstanding, and burying it in a list sorted by date
 * means the oldest, the ones most likely to be forgotten entirely, sink furthest.
 */
export function MeetingLogPage() {
  const { data: user } = useCurrentUser();
  const canLog = can(user, 'meetings', 'edit');
  const dateFormat = useDateFormat();
  const toast = useToast();

  const [type, setType] = useState<MeetingType | ''>('');
  const [outcome, setOutcome] = useState<MeetingStatus | ''>('');

  // A wide window rather than a page: the log is read by scanning, and the range
  // endpoint is bounded at 120 days.
  const [from, to] = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end);
    start.setDate(start.getDate() - 90);
    return [start, end];
  }, []);

  const meetings = useMeetings(from, to);
  const unlogged = useMeetingsNeedingALog();
  const logMeeting = useLogMeeting();

  const [logging, setLogging] = useState<Meeting | null>(null);
  const [draftOutcome, setDraftOutcome] = useState<MeetingStatus>('completed');
  const [draftNotes, setDraftNotes] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const shown = (meetings.data ?? [])
    .filter((meeting) => (type ? meeting.meeting_type === type : true))
    .filter((meeting) => (outcome ? meeting.status === outcome : true))
    .slice()
    .reverse();

  const save = async () => {
    if (!logging || !canLog) return;
    setProblem(null);
    try {
      await logMeeting.mutateAsync({
        id: logging.id,
        outcome: draftOutcome,
        notes: draftNotes.trim() || undefined,
      });
      toast.success('Meeting logged.');
      setLogging(null);
      setDraftNotes('');
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'That log was not saved.');
    }
  };

  return (
    <Page>
      <PageHeader
        title="Meeting log"
        description="What came of every meeting. Times are in your own timezone."
      />

      {unlogged.isError ? (
        <ErrorState
          message="Meetings awaiting a log could not be loaded."
          onRetry={() => {
            void unlogged.refetch();
          }}
        />
      ) : null}
      {unlogged.data && unlogged.data.length > 0 ? (
        <Panel
          title={`${unlogged.data.length} ${unlogged.data.length === 1 ? 'meeting needs' : 'meetings need'} a log`}
          icon={<ClipboardList size={15} />}
        >
          <ul className={styles.pending}>
            {unlogged.data.map((meeting) => (
              <li key={meeting.id} className={styles.pendingItem}>
                <span className={styles.pendingWhen}>
                  {formatDateTime(meeting.scheduled_at, dateFormat)}
                </span>
                <span className={styles.pendingTitle}>{meeting.title}</span>
                <span className={styles.pendingWho}>{meeting.candidate.full_name}</span>
                {canLog ? (
                  <Button
                    size="sm"
                    variant="subtle"
                    onClick={() => {
                      setLogging(meeting);
                      setDraftOutcome('completed');
                      setDraftNotes('');
                    }}
                  >
                    Log it
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      {logging && canLog ? (
        <Panel title={`Log: ${logging.title}`}>
          <div className={styles.form}>
            {problem ? (
              <Alert tone="danger">
                <b>{problem}</b>
              </Alert>
            ) : null}
            <Select
              aria-label="Outcome"
              value={draftOutcome}
              onChange={(event) => {
                setDraftOutcome(event.target.value as MeetingStatus);
              }}
              options={OUTCOME_OPTIONS}
            />
            <Textarea
              rows={3}
              aria-label="Notes"
              placeholder="What happened?"
              value={draftNotes}
              onChange={(event) => {
                setDraftNotes(event.target.value);
              }}
            />
            <div className={styles.formActions}>
              <Button
                variant="ghost"
                onClick={() => {
                  setLogging(null);
                }}
              >
                Cancel
              </Button>
              <Button
                loading={logMeeting.isPending}
                onClick={() => {
                  void save();
                }}
              >
                Save the log
              </Button>
            </div>
          </div>
        </Panel>
      ) : null}

      <Panel>
        <div className={styles.filters}>
          <Select
            aria-label="Type"
            value={type}
            onChange={(event) => {
              setType(event.target.value as MeetingType | '');
            }}
            placeholder="All types"
            options={MEETING_TYPE_OPTIONS}
          />
          <Select
            aria-label="Outcome"
            value={outcome}
            onChange={(event) => {
              setOutcome(event.target.value as MeetingStatus | '');
            }}
            placeholder="All outcomes"
            options={MEETING_STATUS_OPTIONS}
          />
          <span className={styles.count}>Last 90 days</span>
        </div>
      </Panel>

      {meetings.isPending ? (
        <div className={styles.loading}>
          <Spinner size={20} label="Loading the log" />
        </div>
      ) : meetings.isError ? (
        <ErrorState
          message="The meeting log did not load."
          onRetry={() => {
            void meetings.refetch();
          }}
        />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={<ClipboardList size={20} />}
          title="No meetings"
          body={
            type || outcome
              ? 'Nothing matches these filters.'
              : 'Meetings appear here once they are scheduled.'
          }
        />
      ) : (
        <ol className={styles.log}>
          {shown.map((meeting) => (
            <li key={meeting.id} className={styles.entry}>
              <div className={styles.entryHead}>
                <span className={styles.when}>
                  {formatDateTime(meeting.scheduled_at, dateFormat)}
                </span>
                <Chip size="sm" tone={MEETING_TYPE_TONE[meeting.meeting_type]}>
                  {MEETING_TYPE_LABEL[meeting.meeting_type]}
                </Chip>
                <Chip size="sm" tone={MEETING_STATUS_TONE[meeting.status]}>
                  {MEETING_STATUS_LABEL[meeting.status]}
                </Chip>
              </div>
              <p className={styles.title}>{meeting.title}</p>
              <p className={styles.who}>{meeting.candidate.full_name}</p>
              {meeting.outcome_notes ? (
                <p className={styles.outcome}>{meeting.outcome_notes}</p>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </Page>
  );
}
